import type { Category, Size } from '@/types'

export interface Product {
  id: string
  name: string
  description: string
  price: number
  category: Category
  image: string
  inventory: number
  sizes?: Size[]
  discount?: number  // e.g. 0.20 = 20% off
  isNew?: boolean
  rating: number     // 0–5, 0.5 increments
  reviewCount: number
}

export function effectivePrice(product: Product): number {
  if (!product.discount) return product.price
  return Math.round(product.price * (1 - product.discount))
}

export const PRODUCTS: Product[] = [
  {
    id: 'sauce-backpack',
    name: 'Sac à dos',
    description: 'Emportez tout ce dont vous avez besoin avec ce sac élégant et aéré, alliant style et protection optimale pour ordinateur portable et tablette.',
    price: 5999,
    category: 'accessories',
    image: 'images/products/backpack.png',
    inventory: 8,
    rating: 4.5,
    reviewCount: 342,
  },
  {
    id: 'sauce-bike-light',
    name: 'Éclairage vélo',
    description: "Un feu rouge n'est pas l'état désiré dans vos tests, mais c'est un vrai atout dans la vraie vie. Fixez-le à votre vélo et profitez de jusqu'à 10 heures de trajet sécurisé.",
    price: 1799,
    category: 'accessories',
    image: 'images/products/bike-light.png',
    inventory: 0,
    rating: 3.5,
    reviewCount: 89,
  },
  {
    id: 'sauce-bolt-shirt',
    name: 'T-Shirt Bolt',
    description: "Devenez le super-héros du test avec le T-shirt Bolt. Du labo à la rue, ce t-shirt vous gardera au top dans n'importe quel environnement de test.",
    price: 2499,
    category: 'clothing',
    image: 'images/products/bolt-shirt.png',
    inventory: 2,
    sizes: ['S', 'M', 'L'],
    discount: 0.20,
    rating: 4.0,
    reviewCount: 156,
  },
  {
    id: 'sauce-jacket',
    name: 'Veste polaire',
    description: "Il est rare de tomber sur une veste polaire mi-saison à col zippé capable d'encaisser aussi bien le trajet du matin qu'une longue session de tests.",
    price: 8999,
    category: 'clothing',
    image: 'images/products/jacket.png',
    inventory: 0,
    sizes: ['S', 'M', 'L'],
    rating: 4.5,
    reviewCount: 203,
  },
  {
    id: 'sauce-onesie',
    name: 'Combinaison femme',
    description: "Combinaison côtelée à pressions pour la développeuse qui code la nuit. Fermeture bas 3 pressions renforcée, manches et bas ourlés double aiguille — tient le coup.",
    price: 1499,
    category: 'clothing',
    image: 'images/products/onesie.png',
    inventory: 6,
    sizes: ['S', 'M', 'L'],
    rating: 4.0,
    reviewCount: 78,
  },
  {
    id: 'test-allthethings-shirt',
    name: 'T-Shirt Bold',
    description: 'Ce t-shirt classique est parfait pour préparer la révolution au bureau. Achetez-en deux et bénéficiez de 10 % de réduction !',
    price: 2499,
    category: 'clothing',
    image: 'images/products/red-shirt.png',
    inventory: 1,
    sizes: ['S', 'M', 'L'],
    rating: 3.5,
    reviewCount: 124,
  },
  {
    id: 'sauce-headphones',
    name: 'Casque antibruit',
    isNew: true,
    description: "Bloquez le chaos d'une pipeline CI en échec et restez dans la zone. 40 heures d'autonomie, parce que certaines suites de tests prennent du temps.",
    price: 24999,
    category: 'electronics',
    image: 'images/products/headphones.png',
    inventory: 5,
    rating: 4.5,
    reviewCount: 58,
  },
  {
    id: 'sauce-keyboard',
    name: 'Clavier mécanique',
    isNew: true,
    description: 'Rédigez vos assertions plus vite avec une satisfaction tactile garantie. Switches Cherry MX Blue — vos collègues sauront quand vous testez.',
    price: 14999,
    category: 'electronics',
    image: 'images/products/keyboard.png',
    inventory: 3,
    rating: 4.5,
    reviewCount: 41,
  },
  {
    id: 'sauce-webcam',
    name: 'Webcam 4K',
    isNew: true,
    description: "Paraissez impeccable en standup à distance, même quand vos tests ne le sont pas. Autofocus, anneau lumineux intégré et volet de confidentialité pour quand le build plante.",
    price: 10999,
    category: 'electronics',
    image: 'images/products/webcam.png',
    inventory: 9,
    discount: 0.15,
    rating: 4.0,
    reviewCount: 67,
  },
  {
    id: 'sauce-usb-hub',
    name: 'Hub USB-C',
    description: 'Hub 7-en-1 pour connecter tous vos appareils de test simultanément. Un port pour les gouverner tous, un port pour les trouver.',
    price: 3999,
    category: 'electronics',
    image: 'images/products/usb-hub.png',
    inventory: 11,
    rating: 4.0,
    reviewCount: 189,
  },
  {
    id: 'sauce-mouse',
    name: 'Souris sans fil',
    description: "Design ergonomique pour les longues sessions de débogage. Clics silencieux pour cliquer en rage sans réveiller l'équipe.",
    price: 6999,
    category: 'electronics',
    image: 'images/products/mouse.png',
    inventory: 0,
    rating: 4.0,
    reviewCount: 231,
  },
  {
    id: 'sauce-monitor-stand',
    name: 'Support moniteur',
    description: 'Élevez votre écran et votre couverture de tests. Hub USB intégré, gestion des câbles et suffisamment de place pour votre canard en caoutchouc.',
    price: 7999,
    category: 'electronics',
    image: 'images/products/monitor-stand.png',
    inventory: 4,
    rating: 4.5,
    reviewCount: 95,
  },
  {
    id: 'book-clean-code',
    name: 'Clean Code — Robert C. Martin',
    description: "Le classique absolu. Uncle Bob t'explique pourquoi ton code actuel est illisible et comment ne plus jamais recommencer. Spoiler : ça fait mal.",
    price: 3999,
    category: 'books',
    image: 'images/products/book-clean-code.png',
    inventory: 7,
    rating: 5.0,
    reviewCount: 1842,
  },
  {
    id: 'book-tdd',
    name: 'Test-Driven Development — Kent Beck',
    description: "Écrire les tests avant le code ? Oui. Ce livre t'explique pourquoi c'est une idée géniale et non pas une torture. Red, Green, Refactor.",
    price: 3799,
    category: 'books',
    image: 'images/products/book-tdd.png',
    inventory: 10,
    discount: 0.10,
    rating: 4.5,
    reviewCount: 923,
  },
  {
    id: 'book-agile',
    name: 'Scrum — Jeff Sutherland',
    description: 'Le manifeste agile en action. Sprint après sprint, ce livre te montre comment livrer plus vite sans sacrifier la qualité. Ou du moins essayer.',
    price: 2999,
    category: 'books',
    image: 'images/products/book-agile.png',
    inventory: 3,
    rating: 4.0,
    reviewCount: 567,
  },
  {
    id: 'book-devops',
    name: 'The DevOps Handbook',
    description: "Comment briser les silos entre dev et ops pour déployer en continu sans réveiller l'équipe à 3h du matin. Un must pour tout CI/CD enthousiaste.",
    price: 4499,
    category: 'books',
    image: 'images/products/book-devops.png',
    inventory: 0,
    rating: 4.5,
    reviewCount: 634,
  },
  {
    id: 'book-ddd',
    name: 'Domain-Driven Design — Eric Evans',
    isNew: true,
    description: 'Le "Blue Book". Dense, profond, transformateur. Après lecture, tu ne nommeras plus jamais tes variables "data", "info" ou "manager".',
    price: 5499,
    category: 'books',
    image: 'images/products/book-ddd.png',
    inventory: 8,
    rating: 5.0,
    reviewCount: 412,
  },
  {
    id: 'book-playwright',
    name: 'Playwright Testing — Guide Pratique',
    isNew: true,
    description: "Maîtrise Playwright de zéro à héros : sélecteurs, fixtures, API mocking et CI. Le livre que tu aurais voulu avoir avant d'écrire tes premiers tests.",
    price: 3499,
    category: 'books',
    image: 'images/products/book-playwright.png',
    inventory: 14,
    rating: 4.5,
    reviewCount: 138,
  },
]

export const PRODUCTS_MAP = new Map(PRODUCTS.map(p => [p.id, p]))
